import { generateContract } from './data/contracts';
import { refreshJobMarket } from './systems/labor';

// Generate initial available contracts
const initialContracts = [
  generateContract('ws-small', 1),
  generateContract('ws-medium', 1),
].filter(Boolean);

// Initial job market candidates
const initialJobMarket = refreshJobMarket(1);

export const initialGameState = {
  // ── Meta ─────────────────────────────────────────────────────────────────
  tick: 0,
  gameDay: 1,
  gameWeek: 1,
  gameSpeed: 500,       // ms per tick (1 tick = 1 game hour)
  paused: true,
  stage: 1,
  buyoutOfferShown: false,

  // ── Finances ─────────────────────────────────────────────────────────────
  cash: 100000,
  cashHistory: [{ week: 0, balance: 100000 }],
  totalRevenue: 0,
  totalExpenses: 0,
  weeklyBurn: 0,

  // ── Reputation ───────────────────────────────────────────────────────────
  reputation: 10,
  qualityScore: 50,
  reliabilityScore: 50,
  complianceScore: 100,
  brandValue: 0,

  // ── Marketing ────────────────────────────────────────────────────────────
  marketing: {
    weeklyBudget: 0,
    activeCampaigns: [],          // [{id, templateId, type, cost, daysRemaining, totalDays, ...}]
    availableCampaignSlots: [],   // campaign templates available this week
    completedCampaignHistory: [], // bounded — last N completed
    maxCampaignSlots: 3,
    socialSaturation: 0,          // 0–10; past thresholds, effectiveness drops
    scheduledTours: [],           // [{id, scheduledDay, fee, ...}]
    prCrises: [],                 // [{id, type, expiresOnDay, ...}]
    monthlyTourCount: 0,
    lastTourMonthKey: null,       // e.g. "2-1" (gameWeek based) for hard cap reset
  },

  // ── Facility ─────────────────────────────────────────────────────────────
  rooms: [
    {
      id: 'room-01',
      name: 'Greenhouse 1',
      unlocked: true,
      type: 'greenhouse',
      department: 'production',
      capacity: 50,
      isoPos: { col: 0, row: 0 },
      isoPage: 0,
      environment: {
        temperature: 24.0,
        humidity: 65.0,
        co2: 400,
        // Start greenhouse without artificial lights installed/on — player can buy lights early
        lightOn: false,
        lightHours: 18,
      },
      recipe: {
        temperature: 24.0,
        humidity: 60.0,
        co2: 800,
        lightHours: 18,
        // Controls how effective a defoliate action is for this recipe (1.0 = baseline)
        defoliateMultiplier: 1.0,
        nutrientEC: 1.2,
        phTarget: 6.2,
      },
      equipment: {
        hvac:       { health: 100, efficiency: 0.70, automationTier: 0 },
        // lights.installed indicates whether artificial lighting is available in this room
        lights:     { health: 100, efficiency: 0.60, automationTier: 0, installed: false },
        co2sys:     { health: 100, installed: false },
        irrigation: { health: 100, automationTier: 0 },
      },
      batch: {
        id: 'batch-001',
        strainId: 'bagseed-og',
        plantCount: 4,
        stage: 'seedling',
        dayInStage: 1,
        totalAge: 1,
        health: 95,
        stress: 5,
        stressHistory: [],
        yieldPotential: 100,
        thcAccumulation: 0,
        terpeneProfile: 50,
        visualAppeal: 80,
        hermaphroditeRisk: 0,
        moldRisk: 0,
        pestRisk: 0,
        projectedGrams: 0,
        harvestedGrams: null,
        qualityScore: null,
      },
      alerts: [],
      assignedWorkers: ['worker-austin'],
      needsCleaning: false,
      poweredDown: false,
      closedUntilDay: null,
      automation: {
        enabled: false,
        managerId: null,
        defaultStrainId: null,
        defaultPlantCount: 10,
        recipeId: null,
      },
    },
    {
      id: 'room-02',
      name: 'Indoor Veg Room',
      unlocked: false,
      type: 'indoor',
      department: 'production',
      capacity: 50,
      isoPos: { col: 1, row: 0 },
      isoPage: 0,
      unlockCost: 3500,
      environment: { temperature: 24, humidity: 60, co2: 400, lightOn: false, lightHours: 18 },
      recipe: { temperature: 24, humidity: 60, co2: 800, lightHours: 18, nutrientEC: 1.2, phTarget: 6.2 },
      equipment: {
        hvac:       { health: 100, efficiency: 0.80, automationTier: 0 },
        lights:     { health: 100, efficiency: 0.75, automationTier: 0, installed: true },
        co2sys:     { health: 100, installed: false },
        irrigation: { health: 100, automationTier: 0 },
      },
      batch: null,
      alerts: [],
      assignedWorkers: [],
      needsCleaning: false,
      poweredDown: false,
      closedUntilDay: null,
      automation: {
        enabled: false,
        managerId: null,
        defaultStrainId: null,
        defaultPlantCount: 10,
        recipeId: null,
      },
    },
    {
      id: 'room-03',
      name: 'Indoor Flower Room',
      unlocked: false,
      type: 'indoor',
      department: 'production',
      capacity: 50,
      isoPos: { col: 0, row: 1 },
      isoPage: 0,
      unlockCost: 4500,
      environment: { temperature: 22, humidity: 48, co2: 400, lightOn: false, lightHours: 12 },
      recipe: { temperature: 22, humidity: 45, co2: 900, lightHours: 12, nutrientEC: 1.6, phTarget: 6.0 },
      equipment: {
        hvac:       { health: 100, efficiency: 0.85, automationTier: 0 },
        lights:     { health: 100, efficiency: 0.85, automationTier: 0, installed: true },
        co2sys:     { health: 100, installed: false },
        irrigation: { health: 100, automationTier: 0 },
      },
      batch: null,
      alerts: [],
      assignedWorkers: [],
      needsCleaning: false,
      poweredDown: false,
      closedUntilDay: null,
      automation: {
        enabled: false,
        managerId: null,
        defaultStrainId: null,
        defaultPlantCount: 10,
        recipeId: null,
      },
    },
    {
      id: 'room-04',
      name: 'Flower Room 2',
      unlocked: false,
      type: 'industrial',
      department: 'production',
      capacity: 100,
      isoPos: { col: 0, row: 0 },
      isoPage: 1,
      unlockCost: 12000,
      environment: { temperature: 22, humidity: 50, co2: 400, lightOn: false, lightHours: 12 },
      recipe: { temperature: 22, humidity: 45, co2: 1000, lightHours: 12, nutrientEC: 1.8, phTarget: 6.0 },
      equipment: {
        hvac:       { health: 100, efficiency: 0.90, automationTier: 1 },
        lights:     { health: 100, efficiency: 0.90, automationTier: 1, installed: true },
        co2sys:     { health: 100, installed: true },
        irrigation: { health: 100, automationTier: 1 },
      },
      batch: null,
      alerts: [],
      assignedWorkers: [],
      needsCleaning: false,
      poweredDown: false,
      closedUntilDay: null,
      automation: {
        enabled: false,
        managerId: null,
        defaultStrainId: null,
        defaultPlantCount: 10,
        recipeId: null,
      },
    },
    {
      id: 'room-veg-02',
      name: 'Veg Room 02',
      unlocked: false,
      type: 'indoor',
      department: 'production',
      capacity: 50,
      unlockCost: 8000,
      environment: { temperature: 24, humidity: 60, co2: 400, lightOn: false, lightHours: 18 },
      recipe: { temperature: 24, humidity: 60, co2: 800, lightHours: 18, nutrientEC: 1.2, phTarget: 6.2 },
      equipment: {
        hvac:       { health: 100, efficiency: 0.80, automationTier: 0 },
        lights:     { health: 100, efficiency: 0.75, automationTier: 0, installed: true },
        co2sys:     { health: 100, installed: false },
        irrigation: { health: 100, automationTier: 0 },
      },
      batch: null,
      alerts: [],
      assignedWorkers: [],
      needsCleaning: false,
      poweredDown: false,
      closedUntilDay: null,
      automation: { enabled: false, managerId: null, defaultStrainId: null, defaultPlantCount: 10, recipeId: null },
    },
    {
      id: 'room-flower-04',
      name: 'Flower Room 04',
      unlocked: false,
      type: 'industrial',
      department: 'production',
      capacity: 100,
      unlockCost: 20000,
      environment: { temperature: 22, humidity: 50, co2: 400, lightOn: false, lightHours: 12 },
      recipe: { temperature: 22, humidity: 45, co2: 1000, lightHours: 12, nutrientEC: 1.8, phTarget: 6.0 },
      equipment: {
        hvac:       { health: 100, efficiency: 0.90, automationTier: 1 },
        lights:     { health: 100, efficiency: 0.90, automationTier: 1, installed: true },
        co2sys:     { health: 100, installed: true },
        irrigation: { health: 100, automationTier: 1 },
      },
      batch: null,
      alerts: [],
      assignedWorkers: [],
      needsCleaning: false,
      poweredDown: false,
      closedUntilDay: null,
      automation: { enabled: false, managerId: null, defaultStrainId: null, defaultPlantCount: 10, recipeId: null },
    },
    {
      id: 'room-curing',
      name: 'Curing Room',
      unlocked: false,
      type: 'curing',
      department: 'post-production',
      unlockCost: 15000,
      environment: { temperature: 20, humidity: 65 },
      recipe: { temperature: 18, humidity: 62 },
      curingSlots: [],
      equipment: {
        hvac:       { health: 100, efficiency: 0.70, automationTier: 0 },
        humidifier: { installed: false, cost: 4000, label: 'Humidifier' },
      },
      alerts: [],
      assignedWorkers: [],
      poweredDown: false,
      closedUntilDay: null,
    },
    {
      id: 'room-processing',
      name: 'Processing Room',
      unlocked: false,
      type: 'processing',
      department: 'post-production',
      unlockCost: 25000,
      processingQueue: [],
      equipment: {
        basicExtractor: { installed: true,  cost: 0,     label: 'Basic Extractor' },
        rosinPress:     { installed: false, cost: 15000, label: 'Rosin Press' },
        bhoExtractor:   { installed: false, cost: 30000, label: 'BHO Extractor' },
      },
      alerts: [],
      assignedWorkers: [],
      poweredDown: false,
      closedUntilDay: null,
    },
    {
      id: 'room-lab',
      name: 'Research Lab',
      type: 'lab',
      department: 'research',
      isoPage: 1,
      unlocked: false,
      equipment: {
        microscope: { installed: false, cost: 3000, rpBonus: 3, label: 'Microscope' },
        pcr:        { installed: false, cost: 8000, rpBonus: 8, label: 'PCR Machine' },
        sequencer:  { installed: false, cost: 20000, rpBonus: 15, label: 'Gene Sequencer' },
      },
      assignedWorkers: [],
      alerts: [],
    },
  ],
  maxRooms: 4,

  // ── Workers ───────────────────────────────────────────────────────────────
  workers: [
    {
      id: 'worker-austin',
      name: 'Austin Terpz',
      type: 'trimmer',
      skill: 38,
      reliability: 72,
      speed: 55,
      morale: 85,
      mistakeProbability: 0.08,
      wageDemand: 250,
      currentWage: 250,
      loyaltyScore: 90,
      isSaboteur: false,
      assignedRoomId: 'room-01',
      hiredOnDay: 1,
    }
  ],
  jobMarket: initialJobMarket,

  // ── Genetics ─────────────────────────────────────────────────────────────
  unlockedStrains: ['bagseed-og'],
  seedInventory: { 'bagseed-og': 200 },
  phenotypes: [],
  phenoCandidates: null,

  // ── Harvested Inventory ───────────────────────────────────────────────────
  inventory: [],

  // ── Contracts ─────────────────────────────────────────────────────────────
  activeContracts: [],
  availableContracts: initialContracts,
  contractHistory: [],

  // ── Compliance ────────────────────────────────────────────────────────────
  licenseLevel: 0,
  violations: [],
  isShutDown: false,
  shutdownReason: null,
  illegalSalesCount: 0,
  complianceOffenseCount: 0,

  // ── Market ────────────────────────────────────────────────────────────────
  demandModifier: 1.0,            // global multiplier on sale value (0.85–1.30)
  marketPrices: {
    wholesale:      5.0,
    medical:        8.0,
    retail:        14.0,
    blackmarket:   18.0,
    extractRosin:  150,    // midpoint of 100-200 range
    extractWax:     30,    // midpoint of 20-40 range
    extractShatter: 35,    // midpoint of 10-60 range
  },
  priceHistory: [],
  supplyGlut: false,
  marketTrend: 'stable',

  // ── Strain Recipes ────────────────────────────────────────────────────────
  purchasedRecipes: [],  // array of recipe IDs the player has bought

  // ── Technology ────────────────────────────────────────────────────────────
  researchPoints: 0,
  unlockedTech: [],
  activeResearch: null,

  // ── Industry Reviews ──────────────────────────────────────────────────────
  pendingReviews: [],    // [{id, reviewerId, inventorySnapshot, publishDay, channel}]
  publishedReviews: [],  // [{id, reviewerId, reviewerName, score, quote, channel, day, repImpact}]

  // ── Corporate Investment ──────────────────────────────────────────────────
  corporateOfferAvailable: false,      // set true when Stage 2 is reached
  corporateInvestmentAccepted: false,  // permanent flag once deal is taken
  corporateBoughtOut: false,           // true after player buys back equity
  corporateSatisfaction: 75,           // 0-100, CannCorp board happiness
  corporateDirective: null,            // current quarterly directive object
  corporateDirectiveHistory: [],       // completed/failed past directives
  corporateDirectiveMissCount: 0,      // consecutive directive misses
  corporateChannelRestriction: null,   // { channel, type, until } when satisfaction low

  // ── Events & Notifications ────────────────────────────────────────────────
  activeEvents: [],
  notifications: [
    {
      id: 'welcome',
      message: '🌱 Welcome to Canntycoon! Your first seedling is ready. Hit ▶ to start.',
      type: 'info',
      day: 1,
      read: false,
    }
  ],

  // ── Tutorial ──────────────────────────────────────────────────────────────
  tutorial: {
    enabled: true,
    currentStep: 0,      // 0 = WelcomeModal
    welcomeDismissed: false,
    hintDismissed: false,
  },

  // ── UI State ──────────────────────────────────────────────────────────────
  ui: {
    activeScreen: 'facility',
    selectedRoomId: 'room-01',
    selectedFacilityRoomId: null,
    drawerOpen: false,
    modalOpen: null,
    lastSaved: null,
    facilityPage: 0,
  },
};
